import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SolicitudesProcesosService from '../services/SolicitudesProcesosService';
import LoadingSpinner from './common/LoadingSpinner';
import './SolicitudesProcesos.css';

const getProcesoStatus = (proceso) => {
  if (proceso?.error) return { label: 'Error', className: 'error' };
  if (proceso?.fechaFinalizacion) return { label: 'Finalizado', className: 'finalizado' };
  return { label: 'En proceso', className: 'en-proceso' };
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-AR');
};

const formatShortDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const parseBody = (bodyMensaje) => {
  try {
    return JSON.parse(bodyMensaje);
  } catch {
    return null;
  }
};

const getCambioFieldName = (cambio, idx) => cambio?.Campo || cambio?.campo || `Cambio ${idx + 1}`;
const getCambioType = (cambio) => (cambio?.Tipo || cambio?.tipo || 'modificado').toString().toLowerCase();
const getCambioSummary = (cambio) => cambio?.Resumen || cambio?.resumen || cambio?.Tipo || cambio?.tipo || 'Modificado';
const getCambioString = (cambio, camelKey, pascalKey) => {
  const value = cambio?.[pascalKey] ?? cambio?.[camelKey];
  if (value == null) return '';
  return String(value);
};
const getCambioItems = (cambio, camelKey = 'items', pascalKey = 'Items') => {
  const value = cambio?.[pascalKey] ?? cambio?.[camelKey];
  return Array.isArray(value) ? value : [];
};
const getArchivoNombre = (archivo, idx = 0) =>
  archivo?.nombre || archivo?.Nombre || archivo?.FileName || archivo?.fileName || `Archivo ${idx + 1}`;
const uniqueStrings = (values) => {
  const seen = new Set();
  return values.filter((value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

const getLeido = (proceso) => {
  if (proceso?.leido != null) return proceso.leido;
  if (proceso?.Leido != null) return proceso.Leido;
  return null;
};

const getFechaLeido = (proceso) => {
  if (proceso?.fechaLeido != null) return proceso.fechaLeido;
  if (proceso?.FechaLeido != null) return proceso.FechaLeido;
  return null;
};

const isLeido = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
};

const normalizeId = (value) => (value || '').toString().toLowerCase();
const getIdTipoSolicitud = (proceso) => proceso?.idTipoSolicitud ?? proceso?.IdTipoSolicitud ?? null;
const getTipoSolicitud = (proceso) => proceso?.tipoSolicitud ?? proceso?.TipoSolicitud ?? '';
const getEventoSolicitud = (proceso) => {
  const body = parseBody(proceso?.bodyMensaje);
  return (body?.Evento || '').toString();
};
const getTipoNormalized = (proceso) => {
  const ids = {
    creacion: 'a0000001-0000-0000-0000-000000000001',
    actualizacion: 'a0000001-0000-0000-0000-000000000002'
  };
  const idValue = normalizeId(getIdTipoSolicitud(proceso));
  if (idValue === ids.creacion) return 'creacion';
  if (idValue === ids.actualizacion) return 'actualizacion';

  const tipo = getTipoSolicitud(proceso).toLowerCase();
  if (tipo.includes('crea')) return 'creacion';
  if (tipo.includes('actual')) return 'actualizacion';

  const evento = getEventoSolicitud(proceso).toLowerCase();
  if (evento.includes('crea')) return 'creacion';
  if (evento.includes('actual')) return 'actualizacion';

  const descripcion = (proceso?.descripcion || '').toLowerCase();
  if (descripcion.includes('crea')) return 'creacion';
  if (descripcion.includes('actual')) return 'actualizacion';

  return '';
};

const PAGE_SIZE = 20;
const ADMIN_ROLES = ['admin', 'adminregional', 'adminarea'];

const isAdminUser = (currentUser) => {
  const roleName = String(
    currentUser?.currentAccessContext?.nombreRol ||
    currentUser?.currentAccessContext?.roleName ||
    currentUser?.roleName ||
    currentUser?.nombreRol ||
    currentUser?.role ||
    ''
  ).toLowerCase().replace(/\s+/g, '');

  return ADMIN_ROLES.some((role) => roleName.includes(role));
};

const SolicitudesProcesos = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const canDeleteSolicitudes = isAdminUser(user);

  const [procesos, setProcesos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [finalizando, setFinalizando] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterUnread, setFilterUnread] = useState(false);
  const [urlChatGPT, setUrlChatGPT] = useState('');
  const [archivosDb, setArchivosDb] = useState([]);
  const [accionesDb, setAccionesDb] = useState([]);
  const [expandedAccion, setExpandedAccion] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [apiKeyRegional, setApiKeyRegional] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const pageRef = useRef(1);
  const procesosRef = useRef([]);
  const selectedRef = useRef(null);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    procesosRef.current = procesos;
  }, [procesos]);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  const fetchPage = useCallback(async (pageNum, append = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const response = await SolicitudesProcesosService.getSolicitudes({
        page: pageNum,
        pageSize: PAGE_SIZE,
      });
      if (response?.ok) {
        const newItems = response.procesos || [];
        setProcesos((prev) => append ? [...prev, ...newItems] : newItems);
        setTotal(response.total || 0);
        setPage(response.page || pageNum);
        const totalLoaded = append
          ? (pageNum - 1) * PAGE_SIZE + newItems.length
          : newItems.length;
        setHasMore(newItems.length === PAGE_SIZE && totalLoaded < (response.total || 0));
      }
    } catch (error) {
      console.error('Error fetching solicitudes:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
      setInitialLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      fetchPage(1);
    }
  }, [authLoading, user, fetchPage]);

  const refreshProcesos = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setRefreshing(true);
    try {
      const loadedCount = Math.max(PAGE_SIZE, procesosRef.current.length || 0);
      const response = await SolicitudesProcesosService.getSolicitudes({
        page: 1,
        pageSize: loadedCount,
      });

      if (response?.ok) {
        const newItems = response.procesos || [];
        const nextPage = Math.max(1, Math.ceil(newItems.length / PAGE_SIZE));
        const nextTotal = response.total || 0;

        setProcesos(newItems);
        setTotal(nextTotal);
        setPage(nextPage);
        setHasMore(newItems.length < nextTotal);

        const selectedId = selectedRef.current?.id;
        if (selectedId) {
          const updatedSelected = newItems.find((item) => item.id === selectedId);
          if (updatedSelected) {
            setSelected(updatedSelected);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing solicitudes:', error);
    } finally {
      setRefreshing(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return undefined;

    const intervalId = window.setInterval(() => {
      refreshProcesos();
    }, 120000);

    return () => window.clearInterval(intervalId);
  }, [authLoading, user, refreshProcesos]);

  const sentinelRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node || !hasMore || loading) return;
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
          fetchPage(page + 1, true);
        }
      });
      observerRef.current.observe(node);
    },
    [hasMore, loading, page, fetchPage]
  );

  const filteredProcesos = React.useMemo(() => {
    let result = procesos;
    if (filterType !== 'all') {
      result = result.filter((p) => getTipoNormalized(p) === filterType);
    }
    if (filterUnread) {
      result = result.filter((p) => !isLeido(getLeido(p)));
    }
    if (!searchTerm.trim()) return result;
    const term = searchTerm.trim().toLowerCase();
    return result.filter((p) =>
      (p.descripcion || '').toLowerCase().includes(term) ||
      (p.tipoSolicitud || '').toLowerCase().includes(term) ||
      (p.nombreSolicitante || '').toLowerCase().includes(term) ||
      (p.nombreMateria || '').toLowerCase().includes(term) ||
      (p.nombreArea || '').toLowerCase().includes(term)
    );
  }, [procesos, searchTerm, filterType, filterUnread]);

  const handleSelectProceso = async (proceso) => {
    setSelected(proceso);
    setMobileShowDetail(true);
    const body = parseBody(proceso.bodyMensaje);
    const data = body?.Data || {};
    const asistenteDb = proceso.asistente || {};
    const url = asistenteDb.gptClasicoURLPublica || asistenteDb.GPTClasicoURLPublica || data.GPTClasicoURLPublica || '';
    setUrlChatGPT(url || '');
    setArchivosDb([]);
    setAccionesDb([]);
    setExpandedAccion(null);
    // Fetch archivos and acciones in parallel
    try {
      const [archivosRes, accionesRes] = await Promise.all([
        SolicitudesProcesosService.getArchivos(proceso.id),
        SolicitudesProcesosService.getAcciones(proceso.id)
      ]);
      if (archivosRes?.ok) setArchivosDb(archivosRes.archivos || []);
      if (accionesRes?.ok) {
        setAccionesDb(accionesRes.acciones || []);
        setApiKeyRegional(accionesRes.apiKeyRegional || null);
      }
    } catch (error) {
      console.error('Error fetching archivos/acciones:', error);
    }
  };

  const handleMarkAsRead = async () => {
    if (!selected || isLeido(getLeido(selected)) || markingAsRead) return;

    setMarkingAsRead(true);
    try {
      const response = await SolicitudesProcesosService.marcarLeido(selected.id);
      const fechaLeido = response?.fechaLeido || new Date().toISOString();

      setProcesos((prev) =>
        prev.map((p) => (
          p.id === selected.id
            ? { ...p, leido: true, fechaLeido }
            : p
        ))
      );
      setSelected((prev) => (
        prev
          ? { ...prev, leido: true, fechaLeido }
          : prev
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    } finally {
      setMarkingAsRead(false);
    }
  };

  const handleDeleteProceso = async (proceso) => {
    if (!canDeleteSolicitudes || !proceso?.id || deletingId) return;

    const confirmed = window.confirm(`¿Eliminar la solicitud "${proceso.descripcion || 'seleccionada'}"?`);
    if (!confirmed) return;

    setDeletingId(proceso.id);
    try {
      const response = await SolicitudesProcesosService.eliminar(proceso.id);
      if (response?.ok) {
        setProcesos((prev) => prev.filter((p) => p.id !== proceso.id));
        setTotal((prev) => Math.max(0, prev - 1));

        if (selectedRef.current?.id === proceso.id) {
          setSelected(null);
          setMobileShowDetail(false);
          setUrlChatGPT('');
          setArchivosDb([]);
          setAccionesDb([]);
          setExpandedAccion(null);
          setApiKeyRegional(null);
        }
      }
    } catch (error) {
      console.error('Error deleting solicitud:', error);
      window.alert('No se pudo eliminar la solicitud.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleFinalizar = async () => {
    if (!selected || finalizando) return;
    if (!urlChatGPT.trim()) return;
    setFinalizando(true);
    try {
      const response = await SolicitudesProcesosService.finalizar(selected.id, urlChatGPT.trim());
      if (response?.ok) {
        const now = new Date().toISOString();
        setProcesos((prev) =>
          prev.map((p) => (
            p.id === selected.id
              ? {
                  ...p,
                  fechaFinalizacion: now,
                  leido: true,
                  fechaLeido: getFechaLeido(p) || now
                }
              : p
          ))
        );
        setSelected((prev) => (
          prev
            ? {
                ...prev,
                fechaFinalizacion: now,
                leido: true,
                fechaLeido: getFechaLeido(prev) || now
              }
            : prev
        ));
        setUrlChatGPT('');
      }
    } catch (error) {
      console.error('Error finalizando proceso:', error);
    } finally {
      setFinalizando(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="solicitudes-page">
        <div className="solicitudes-loading">
          <LoadingSpinner message="Cargando solicitudes..." />
        </div>
      </div>
    );
  }

  const renderDetailContent = () => {
    if (!selected) return null;

    const body = parseBody(selected.bodyMensaje);
    const rawData = body?.Data || {};
    const asistenteDb = selected.asistente || {};
    const data = { ...rawData };
    if (data.GPTClasicoInstrucciones == null && asistenteDb.gptClasicoInstrucciones != null) data.GPTClasicoInstrucciones = asistenteDb.gptClasicoInstrucciones;
    if (data.GPTClasicoDescripcion == null && asistenteDb.gptClasicoDescripcion != null) data.GPTClasicoDescripcion = asistenteDb.gptClasicoDescripcion;
    if (data.GPTClasicoFraseIniciarConversacion == null && asistenteDb.gptClasicoFraseIniciarConversacion != null) data.GPTClasicoFraseIniciarConversacion = asistenteDb.gptClasicoFraseIniciarConversacion;
    if (data.GPTClasicoNombre == null && asistenteDb.gptClasicoNombre != null) data.GPTClasicoNombre = asistenteDb.gptClasicoNombre;
    if (data.GPTClasicoModelo == null && asistenteDb.gptClasicoModelo != null) data.GPTClasicoModelo = asistenteDb.gptClasicoModelo;
    if (data.GPTClasicoURLPublica == null && asistenteDb.gptClasicoURLPublica != null) data.GPTClasicoURLPublica = asistenteDb.gptClasicoURLPublica;
    const archivos = body?.Archivos || [];
    const cambios = Array.isArray(body?.Cambios) ? body.Cambios : (Array.isArray(body?.cambios) ? body.cambios : []);
    const status = getProcesoStatus(selected);
    const isEnProceso = !selected.fechaFinalizacion && !selected.error;
    const solicitudLeida = isLeido(getLeido(selected));
    const fechaLeido = getFechaLeido(selected);
    const tipoSolicitud = getTipoNormalized(selected);
    const esActualizacion = tipoSolicitud === 'actualizacion' || (body?.Evento || '').toString().toLowerCase().includes('actual');
    const cambiosCampos = cambios.filter((cambio) => getCambioFieldName(cambio, 0).toLowerCase() !== 'archivos');
    const cambiosArchivos = cambios.filter((cambio) => getCambioFieldName(cambio, 0).toLowerCase() === 'archivos');
    const requestedFileNames = uniqueStrings(archivos.map((archivo, idx) => getArchivoNombre(archivo, idx)));
    const filesToReplace = uniqueStrings(
      cambiosArchivos
        .filter((cambio) => ['reemplazar', 'reemplazo'].includes(getCambioType(cambio)))
        .flatMap((cambio) => {
          const nextItems = getCambioItems(cambio, 'itemsNuevos', 'ItemsNuevos');
          return nextItems.length > 0 ? nextItems : getCambioItems(cambio);
        })
    );
    const filesToRemove = uniqueStrings(
      cambiosArchivos
        .filter((cambio) => ['quitar', 'eliminar', 'remover'].includes(getCambioType(cambio)))
        .flatMap((cambio) => {
          const previousItems = getCambioItems(cambio, 'itemsAnteriores', 'ItemsAnteriores');
          return previousItems.length > 0 ? previousItems : getCambioItems(cambio);
        })
    );
    const explicitFilesToAdd = uniqueStrings(
      cambiosArchivos
        .filter((cambio) => ['agregar', 'agregados'].includes(getCambioType(cambio)))
        .flatMap((cambio) => {
          const nextItems = getCambioItems(cambio, 'itemsNuevos', 'ItemsNuevos');
          return nextItems.length > 0 ? nextItems : getCambioItems(cambio);
        })
    );
    const filesToAdd = uniqueStrings(
      (explicitFilesToAdd.length > 0 ? explicitFilesToAdd : requestedFileNames)
        .filter((fileName) => !filesToReplace.some((replaceName) => replaceName.toLowerCase() === fileName.toLowerCase()))
    );
    const hasOperationalGuide =
      cambiosCampos.length > 0 ||
      filesToReplace.length > 0 ||
      filesToAdd.length > 0 ||
      filesToRemove.length > 0;

    return (
      <div className="solicitudes-detail">
        <button
          className="solicitudes-detail-back"
          onClick={() => setMobileShowDetail(false)}
        >
          <i className="fas fa-arrow-left"></i> Volver a la lista
        </button>

        <h2>{selected.descripcion}</h2>

        <div className="solicitudes-detail-status-row">
          <span className={`procesos-status ${status.className}`}>{status.label}</span>
          <span className="solicitudes-detail-date">
            <i className="far fa-clock"></i> {formatDate(selected.fechaCreacion)}
          </span>
          {solicitudLeida ? (
            <span className="solicitudes-read-badge">
              <i className="fas fa-check"></i> {fechaLeido ? `Leida el ${formatShortDate(fechaLeido)}` : 'Leida'}
            </span>
          ) : (
            <button
              type="button"
              className="solicitudes-read-btn"
              onClick={handleMarkAsRead}
              disabled={markingAsRead}
            >
              {markingAsRead ? (
                <><i className="fas fa-spinner fa-spin"></i> Leyendo...</>
              ) : (
                <><i className="fas fa-book-open"></i> Leer</>
              )}
            </button>
          )}
        </div>

        {selected.error && (
          <div className="solicitudes-detail-error">
            <strong>Error:</strong> {selected.error}
          </div>
        )}

        {/* Datos de la solicitud */}
        {body && (
          <section className="solicitudes-section">
            <h3 className="solicitudes-section-title">
              <i className="fas fa-file-alt"></i> Datos de la solicitud
            </h3>
            <div className="solicitudes-section-grid">
              <div className="solicitudes-field">
                <label>Evento</label>
                <input type="text" readOnly value={body.Evento || '-'} />
              </div>
              <div className="solicitudes-field">
                <label>Tipo de Asistente</label>
                <input type="text" readOnly value={body.TipoAsistente || '-'} />
              </div>
              <div className="solicitudes-field">
                <label>Profesor</label>
                <input type="text" readOnly value={selected.nombreSolicitante || '-'} />
              </div>
              <div className="solicitudes-field">
                <label>Regional</label>
                <input type="text" readOnly value={selected.nombreRegional || '-'} />
              </div>
              <div className="solicitudes-field">
                <label>Area</label>
                <input type="text" readOnly value={selected.nombreArea || '-'} />
              </div>
              <div className="solicitudes-field">
                <label>Materia</label>
                <input type="text" readOnly value={selected.nombreMateria || '-'} />
              </div>
            </div>
          </section>
        )}

        {hasOperationalGuide && (
          <section className="solicitudes-section solicitudes-guide-section">
            <h3 className="solicitudes-section-title">
              <i className="fas fa-list-check"></i> Qué tenés que hacer
            </h3>
            <div className="solicitudes-guide-grid">
              {cambiosCampos.length > 0 && (
                <div className="solicitudes-guide-card">
                  <div className="solicitudes-guide-card-header">
                    <span className="solicitudes-guide-card-title">Editar en ChatGPT</span>
                    <span className="solicitudes-guide-chip edit">{cambiosCampos.length}</span>
                  </div>
                  <div className="solicitudes-guide-list">
                    {cambiosCampos.map((cambio, idx) => {
                      const fieldName = getCambioFieldName(cambio, idx);
                      const previousValue = getCambioString(cambio, 'valorAnterior', 'ValorAnterior');
                      const nextValue = getCambioString(cambio, 'valorNuevo', 'ValorNuevo');
                      const summary = getCambioSummary(cambio);
                      return (
                        <div key={`edit-${fieldName}-${idx}`} className="solicitudes-guide-item">
                          <div className="solicitudes-guide-item-title-row">
                            <span className="solicitudes-guide-item-title">{fieldName}</span>
                            <span className="solicitudes-guide-item-badge">Editar</span>
                          </div>
                          {(previousValue || nextValue) ? (
                            <div className="solicitudes-guide-diff">
                              <div className="solicitudes-guide-diff-block">
                                <span className="solicitudes-guide-diff-label">Antes</span>
                                <div className="solicitudes-guide-diff-value">{previousValue || 'Vacío'}</div>
                              </div>
                              <div className="solicitudes-guide-diff-block">
                                <span className="solicitudes-guide-diff-label">Ahora</span>
                                <div className="solicitudes-guide-diff-value">{nextValue || 'Vacío'}</div>
                              </div>
                            </div>
                          ) : (
                            <p className="solicitudes-guide-item-summary">{summary}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {filesToReplace.length > 0 && (
                <div className="solicitudes-guide-card">
                  <div className="solicitudes-guide-card-header">
                    <span className="solicitudes-guide-card-title">Archivos para reemplazar</span>
                    <span className="solicitudes-guide-chip replace">{filesToReplace.length}</span>
                  </div>
                  <div className="solicitudes-guide-list">
                    {filesToReplace.map((fileName) => (
                      <div key={`replace-${fileName}`} className="solicitudes-guide-item">
                        <div className="solicitudes-guide-item-title-row">
                          <span className="solicitudes-guide-item-title">{fileName}</span>
                          <span className="solicitudes-guide-item-badge replace">Quitar y subir</span>
                        </div>
                        <p className="solicitudes-guide-item-summary">
                          Quitá la versión actual en ChatGPT y subí la nueva versión descargándola desde la sección de archivos.
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {filesToAdd.length > 0 && (
                <div className="solicitudes-guide-card">
                  <div className="solicitudes-guide-card-header">
                    <span className="solicitudes-guide-card-title">Archivos para agregar</span>
                    <span className="solicitudes-guide-chip add">{filesToAdd.length}</span>
                  </div>
                  <div className="solicitudes-guide-list">
                    {filesToAdd.map((fileName) => (
                      <div key={`add-${fileName}`} className="solicitudes-guide-item">
                        <div className="solicitudes-guide-item-title-row">
                          <span className="solicitudes-guide-item-title">{fileName}</span>
                          <span className="solicitudes-guide-item-badge add">Agregar</span>
                        </div>
                        <p className="solicitudes-guide-item-summary">
                          Subí este archivo en ChatGPT. Si necesitás el binario, descargalo desde la sección de archivos.
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {filesToRemove.length > 0 && (
                <div className="solicitudes-guide-card">
                  <div className="solicitudes-guide-card-header">
                    <span className="solicitudes-guide-card-title">Archivos para quitar</span>
                    <span className="solicitudes-guide-chip remove">{filesToRemove.length}</span>
                  </div>
                  <div className="solicitudes-guide-list">
                    {filesToRemove.map((fileName) => (
                      <div key={`remove-${fileName}`} className="solicitudes-guide-item">
                        <div className="solicitudes-guide-item-title-row">
                          <span className="solicitudes-guide-item-title">{fileName}</span>
                          <span className="solicitudes-guide-item-badge remove">Quitar</span>
                        </div>
                        <p className="solicitudes-guide-item-summary">
                          Eliminá este archivo de ChatGPT. Esta solicitud lo marcó como archivo a quitar.
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {esActualizacion && filesToRemove.length === 0 && (
              <div className="solicitudes-guide-note">
                <i className="fas fa-circle-info"></i>
                <span>Esta solicitud no marcó archivos para quitar de forma explícita.</span>
              </div>
            )}
          </section>
        )}

        {cambios.length > 0 && (
          <section className="solicitudes-section">
            <h3 className="solicitudes-section-title">
              <i className="fas fa-pen"></i> Cambios solicitados
            </h3>
            <div className="solicitudes-section-fields">
              {cambios.map((cambio, idx) => {
                const fieldName = getCambioFieldName(cambio, idx);
                const summary = getCambioSummary(cambio);
                const items = getCambioItems(cambio);
                const previousValue = getCambioString(cambio, 'valorAnterior', 'ValorAnterior');
                const nextValue = getCambioString(cambio, 'valorNuevo', 'ValorNuevo');
                const previousItems = getCambioItems(cambio, 'itemsAnteriores', 'ItemsAnteriores');
                const nextItems = getCambioItems(cambio, 'itemsNuevos', 'ItemsNuevos');
                const hasStructuredDiff = previousValue || nextValue || previousItems.length > 0 || nextItems.length > 0;
                const textValue = items.length > 0 ? items.join('\n') : summary;
                return (
                  <div
                    key={`${fieldName}-${idx}`}
                    className={`solicitudes-field ${(items.length > 0 || hasStructuredDiff) ? 'solicitudes-field-full' : ''}`}
                  >
                    <label>{fieldName}</label>
                    {hasStructuredDiff ? (
                      <div className="solicitudes-change-diff">
                        {(previousValue || nextValue) && (
                          <div className="solicitudes-change-diff-grid">
                            <div className="solicitudes-change-diff-block">
                              <span className="solicitudes-change-diff-label">Antes</span>
                              <textarea readOnly value={previousValue || 'Vacío'} rows={Math.min(Math.max((previousValue || 'Vacío').split('\n').length, 2), 6)} />
                            </div>
                            <div className="solicitudes-change-diff-block">
                              <span className="solicitudes-change-diff-label">Ahora</span>
                              <textarea readOnly value={nextValue || 'Vacío'} rows={Math.min(Math.max((nextValue || 'Vacío').split('\n').length, 2), 6)} />
                            </div>
                          </div>
                        )}
                        {(previousItems.length > 0 || nextItems.length > 0) && (
                          <div className="solicitudes-change-diff-grid">
                            {previousItems.length > 0 && (
                              <div className="solicitudes-change-diff-block">
                                <span className="solicitudes-change-diff-label">Antes</span>
                                <textarea readOnly value={previousItems.join('\n')} rows={Math.min(Math.max(previousItems.length, 3), 8)} />
                              </div>
                            )}
                            {nextItems.length > 0 && (
                              <div className="solicitudes-change-diff-block">
                                <span className="solicitudes-change-diff-label">Ahora</span>
                                <textarea readOnly value={nextItems.join('\n')} rows={Math.min(Math.max(nextItems.length, 3), 8)} />
                              </div>
                            )}
                          </div>
                        )}
                        <div className="solicitudes-change-summary">{summary}</div>
                      </div>
                    ) : items.length > 0 ? (
                      <textarea readOnly value={textValue} rows={Math.min(Math.max(items.length, 3), 8)} />
                    ) : (
                      <input type="text" readOnly value={textValue} />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Datos del GPT */}
        {body && Object.keys(data).length > 0 && (
          <section className="solicitudes-section">
            <h3 className="solicitudes-section-title">
              <i className="fas fa-robot"></i> Datos del GPT
            </h3>
            <div className="solicitudes-section-fields">
              {(() => {
                const hiddenKeys = ['GPTClasicoURLPublica', 'Descripcion', 'NicknameUrl', 'PasswordAsistente', 'IdRegional', 'IdArea', 'IdMateria', 'GPTClasicoNombre', 'IdTipoAsistente', 'ConPassword', 'Publico'];
                const entries = Object.entries(data).filter(([key]) => !hiddenKeys.includes(key));
                const nonBool = entries.filter(([, v]) => typeof v !== 'boolean');
                const booleans = entries.filter(([, v]) => typeof v === 'boolean');
                return (
                  <>
                    {nonBool.map(([key, value]) => {
                      const strValue = value != null ? String(value) : '-';
                      const forceFullTextarea = key === 'GPTClasicoInstrucciones' || key === 'GPTClasicoDescripcion';
                      const forceFullRow = key === 'GPTClasicoModelo';
                      const isLong = forceFullTextarea || forceFullRow || strValue.length > 120;
                      return (
                        <div key={key} className={`solicitudes-field ${(isLong || forceFullRow) ? 'solicitudes-field-full' : ''}`}>
                          <label>{key}</label>
                          {forceFullTextarea ? (
                            <textarea readOnly value={strValue} rows={6} />
                          ) : isLong ? (
                            <textarea readOnly value={strValue} rows={4} />
                          ) : (
                            <input type="text" readOnly value={strValue} />
                          )}
                        </div>
                      );
                    })}
                    {booleans.length > 0 && (
                      <div className="solicitudes-field-full solicitudes-checkboxes-group">
                        {booleans.map(([key, value]) => (
                          <div key={key} className="solicitudes-field solicitudes-field-checkbox">
                            <label>
                              <input type="checkbox" checked={value} readOnly />
                              {key}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}

            </div>
          </section>
        )}

        {/* Archivos */}
        {(archivos.length > 0 || archivosDb.length > 0) && (
          <section className="solicitudes-section">
            <div className="solicitudes-section-header-row">
              <h3 className="solicitudes-section-title">
                <i className="fas fa-paperclip"></i> Archivos ({archivosDb.length || archivos.length})
              </h3>
              {archivosDb.length > 0 && (
                <button
                  className="solicitudes-download-all-btn"
                  onClick={async () => {
                    setDownloadingFile('zip');
                    try { await SolicitudesProcesosService.downloadAllFiles(selected.id); }
                    catch (e) { console.error('Error downloading zip:', e); }
                    finally { setDownloadingFile(null); }
                  }}
                  disabled={downloadingFile != null}
                >
                  {downloadingFile === 'zip' ? (
                    <><i className="fas fa-spinner fa-spin"></i> Descargando...</>
                  ) : (
                    <><i className="fas fa-file-archive"></i> Descargar todos (.zip)</>
                  )}
                </button>
              )}
            </div>
            <div className="solicitudes-archivos-list">
              {(archivosDb.length > 0 ? archivosDb : archivos).map((archivo, idx) => {
                const fileId = archivo.id;
                const fileName = archivo.nombre || archivo.Nombre || archivo.FileName || `Archivo ${idx + 1}`;
                const bodyArchivo = archivos[idx];
                return (
                  <div key={fileId || idx} className="solicitudes-archivo-item">
                    <i className="fas fa-file"></i>
                    <div className="solicitudes-archivo-info">
                      <span className="solicitudes-archivo-name">{fileName}</span>
                      {bodyArchivo && (
                        <span className="solicitudes-archivo-meta">
                          {bodyArchivo.ContentType && <span>{bodyArchivo.ContentType}</span>}
                          {bodyArchivo.Length != null && <span>{formatFileSize(bodyArchivo.Length)}</span>}
                        </span>
                      )}
                    </div>
                    {fileId && (
                      <button
                        className="solicitudes-download-btn"
                        onClick={async () => {
                          setDownloadingFile(fileId);
                          try { await SolicitudesProcesosService.downloadFile(selected.id, fileId, fileName); }
                          catch (e) { console.error('Error downloading file:', e); }
                          finally { setDownloadingFile(null); }
                        }}
                        disabled={downloadingFile != null}
                        title="Descargar archivo"
                      >
                        {downloadingFile === fileId ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-download"></i>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Acciones */}
        {accionesDb.length > 0 && (
          <section className="solicitudes-section">
            <h3 className="solicitudes-section-title">
              <i className="fas fa-bolt"></i> Acciones ({accionesDb.length})
            </h3>
            {apiKeyRegional && (
              <div className="solicitudes-row" style={{ marginBottom: '12px' }}>
                <div className="solicitudes-field" style={{ flex: 2 }}>
                  <label className="solicitudes-label">API Key Regional</label>
                  <input className="solicitudes-input" type="text" readOnly value={apiKeyRegional} />
                </div>
                <div className="solicitudes-field" style={{ flex: 1 }}>
                  <label className="solicitudes-label">Header</label>
                  <input className="solicitudes-input" type="text" readOnly value="X-Api-Key" />
                </div>
              </div>
            )}
            <div className="solicitudes-acciones-list">
              {accionesDb.map((accion) => {
                const isExpanded = expandedAccion === accion.id;
                let parsedContent = null;
                try { parsedContent = JSON.parse(accion.content); } catch { /* not json */ }
                const displayName = parsedContent?.info?.title || `Acción`;
                const version = parsedContent?.info?.version || '';
                return (
                  <div key={accion.id} className="solicitudes-accion-item">
                    <button
                      type="button"
                      className="solicitudes-accion-header"
                      onClick={() => setExpandedAccion(isExpanded ? null : accion.id)}
                    >
                      <div className="solicitudes-accion-info">
                        <i className="fas fa-cog"></i>
                        <span className="solicitudes-accion-name">{displayName}</span>
                        {version && <span className="solicitudes-accion-version">v{version}</span>}
                      </div>
                      <div className="solicitudes-accion-meta">
                        {accion.fechaAlta && (
                          <span className="solicitudes-accion-date">{formatDate(accion.fechaAlta)}</span>
                        )}
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="solicitudes-accion-content">
                        <textarea
                          readOnly
                          value={parsedContent ? JSON.stringify(parsedContent, null, 2) : accion.content}
                          rows={16}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Finalizar solicitud */}
        {isEnProceso && (
          <section className="solicitudes-section">
            <h3 className="solicitudes-section-title">
              <i className="fas fa-flag-checkered"></i> Finalizar solicitud
            </h3>
            <div className="solicitudes-section-fields">
              <div className="solicitudes-field solicitudes-field-full">
                <label>GPTClasicoURLPublica</label>
                <input
                  type="url"
                  placeholder="https://chatgpt.com/g/g-..."
                  value={urlChatGPT}
                  onChange={(e) => setUrlChatGPT(e.target.value)}
                />
              </div>
            </div>
            <button
              className="solicitudes-finalizar-btn"
              onClick={handleFinalizar}
              disabled={finalizando || !urlChatGPT.trim()}
            >
              {finalizando ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Finalizando...
                </>
              ) : (
                <>
                  <i className="fas fa-check-circle"></i>
                  Finalizar solicitud
                </>
              )}
            </button>
          </section>
        )}

        {selected.fechaFinalizacion && (
          <div className="solicitudes-detail-finalized">
            <i className="fas fa-check-circle"></i>
            <span>Finalizado el {formatDate(selected.fechaFinalizacion)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="solicitudes-page">
      <header className="solicitudes-header">
        <button className="solicitudes-back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left"></i> Volver
        </button>
        <h1 className="solicitudes-title">
          <i className="fas fa-clipboard-list"></i>
          Solicitudes
        </h1>
        <div className="solicitudes-header-actions">
          <span className="solicitudes-count">{total} solicitudes</span>
          <button
            type="button"
            className="solicitudes-refresh-btn"
            onClick={refreshProcesos}
            disabled={loading || refreshing}
          >
            <i className={`fas ${refreshing ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`}></i>
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>
      </header>

      <div className="solicitudes-split">
        <aside className={`solicitudes-list-panel ${mobileShowDetail ? 'hidden-mobile' : ''}`}>
          <div className="solicitudes-filters">
            <button
              type="button"
              className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => {
                setFilterType('all');
                setFilterUnread(false);
              }}
            >
              Todas
            </button>
            <button
              type="button"
              className={`filter-btn ${filterType === 'creacion' ? 'active' : ''}`}
              onClick={() => {
                setFilterType('creacion');
                setFilterUnread(false);
              }}
            >
              Creación
            </button>
            <button
              type="button"
              className={`filter-btn ${filterType === 'actualizacion' ? 'active' : ''}`}
              onClick={() => {
                setFilterType('actualizacion');
                setFilterUnread(false);
              }}
            >
              Actualización
            </button>
            <button
              type="button"
              className={`filter-btn ${filterUnread ? 'active' : ''}`}
              onClick={() => {
                setFilterUnread((prev) => {
                  const next = !prev;
                  if (next) setFilterType('all');
                  return next;
                });
              }}
            >
              No leídas
            </button>
          </div>
          <div className="solicitudes-search">
            <i className="fas fa-search solicitudes-search-icon"></i>
            <input
              type="text"
              className="solicitudes-search-input"
              placeholder="Buscar por nombre, materia, descripcion..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="solicitudes-search-clear"
                onClick={() => setSearchTerm('')}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          <div className="solicitudes-list-scroll">
            {filteredProcesos.length === 0 && !loading && (
              <div className="solicitudes-empty">
                {searchTerm ? 'No se encontraron resultados.' : 'No hay solicitudes.'}
              </div>
            )}
            {filteredProcesos.map((proceso) => {
              const status = getProcesoStatus(proceso);
              const isSelected = selected?.id === proceso.id;
              const isDeleting = deletingId === proceso.id;
              return (
                <div
                  key={proceso.id}
                  role="button"
                  tabIndex={0}
                  className={`solicitudes-item ${!isLeido(getLeido(proceso)) ? 'unread' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelectProceso(proceso)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSelectProceso(proceso);
                    }
                  }}
                >
                  <div className="solicitudes-item-row">
                    <span className="solicitudes-item-desc">{proceso.descripcion}</span>
                    <div className="solicitudes-item-actions">
                      <span className={`procesos-status ${status.className}`}>{status.label}</span>
                      {canDeleteSolicitudes && (
                        <button
                          type="button"
                          className="solicitudes-delete-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteProceso(proceso);
                          }}
                          onKeyDown={(event) => event.stopPropagation()}
                          disabled={deletingId != null}
                          title="Eliminar solicitud"
                          aria-label={`Eliminar solicitud ${proceso.descripcion || ''}`.trim()}
                        >
                          {isDeleting ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fas fa-trash"></i>
                          )}
                          <span>Eliminar</span>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="solicitudes-item-meta">
                    {proceso.nombreSolicitante && (
                      <span className="solicitudes-item-solicitante">
                        <i className="fas fa-user"></i> {proceso.nombreSolicitante}
                      </span>
                    )}
                    {proceso.nombreMateria && (
                      <span className="solicitudes-item-materia">
                        <i className="fas fa-book"></i> {proceso.nombreMateria}
                      </span>
                    )}
                  </div>
                  <div className="solicitudes-item-date">
                    <i className="far fa-clock"></i> {formatDate(proceso.fechaCreacion)}
                  </div>
                </div>
              );
            })}
            {hasMore && (
              <div ref={sentinelRef} className="solicitudes-sentinel">
                {loading && <LoadingSpinner message="Cargando..." />}
              </div>
            )}
          </div>
        </aside>

        <main className={`solicitudes-detail-panel ${!mobileShowDetail ? 'hidden-mobile' : ''}`}>
          {!selected ? (
            <div className="solicitudes-no-selection">
              <i className="fas fa-inbox"></i>
              <p>Seleccioná una solicitud para ver su detalle.</p>
            </div>
          ) : (
            renderDetailContent()
          )}
        </main>
      </div>
    </div>
  );
};

export default SolicitudesProcesos;
